import { Component } from 'react';
import { KeyValue } from '@mews/enums';
import Mask, { Direction, ValueWithSelection } from '../masked/Mask';
import maskedValue from '../masked/MaskedValue';
import { getFirstValueDifference } from './helpers';
import { InputContainer } from './style';

export interface MaskedInputProps {
    name: string;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (event: React.ChangeEvent<HTMLInputElement> | React.MouseEvent, value: string | number) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    showPatternPlaceholder?: boolean;
    placeholder?: string;
    mask: Mask;
    value?: string;
    type?: string;
    inputElementRef?: (c: HTMLInputElement) => void;
}

const defaultProps = {
    showPatternPlaceholder: false,
    value: '',
};

const looksLikeDateRegExp = /^[0-9]{1,4}((\.|,|-|\/)( )?[0-9]{1,4}){2}$/;

export interface MaskedInputState {
    focused: boolean;
    vs: ValueWithSelection;
}

export interface InputFieldProps {
    name: string;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onInput?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onChange?: (event: React.ChangeEvent<HTMLInputElement> | React.MouseEvent, value: string | number) => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    placeholder?: React.ReactNode;
    value?: string | number;
}

export default (InputElement: React.ComponentType<InputFieldProps>) =>
    class MaskedInput extends Component<MaskedInputProps, MaskedInputState> {
        static readonly defaultProps = defaultProps;

        state = { focused: false, vs: { value: '', selection: { start: 0, end: 0 } } };

        inputElement?: HTMLInputElement;

        isPhoneInput = false;
        phoneInput = {
            previousSelectionStart: 0,
            previousSelectionEnd: 0,
            previousValue: '',
        };

        componentDidMount() {
            const { value } = this.props;
            if (typeof value !== 'undefined') {
                this.setState({ vs: { value, selection: { start: value.length, end: value.length } } });
            }
        }

        componentDidUpdate(prevProps: MaskedInputProps, { vs: { value: prevStateValue } }: MaskedInputState) {
            const { value: propsValue } = this.props;

            const { start, end } = this.state.vs.selection;
            if (
                typeof propsValue !== 'undefined' &&
                propsValue !== prevStateValue &&
                propsValue !== this.state.vs.value
            ) {
                this.setState({ vs: { value: propsValue, selection: { start, end } } });
            }

            if (this.inputElement && this.state.focused) {
                const { inputElement } = this;

                inputElement.selectionStart = start;
                inputElement.selectionEnd = end;
            }
        }

        handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (this.inputElement) {
                const { mask } = this.props;
                const { inputElement } = this;
                const { value, selectionStart: start, selectionEnd: end } = inputElement;
                const { key } = e;

                let newValueWithSelection;

                let handled = false;
                if (start !== null && end !== null) {
                    if (key === KeyValue.Unidentified) {
                        this.isPhoneInput = true;
                        this.phoneInput = {
                            previousSelectionStart: start,
                            previousSelectionEnd: end,
                            previousValue: inputElement.value,
                        };
                    } else if (start < end) {
                        if (key === KeyValue.Delete || key === KeyValue.Backspace) {
                            handled = true;
                            newValueWithSelection = maskedValue.select(value, { start, end });
                            newValueWithSelection = maskedValue.remove(newValueWithSelection, mask);
                        }
                    } else if (key === KeyValue.Backspace) {
                        handled = true;
                        newValueWithSelection = maskedValue.select(value, { start: start - 1, end });
                        newValueWithSelection = maskedValue.remove(newValueWithSelection, mask);
                    } else if (key === KeyValue.Delete) {
                        handled = true;
                        newValueWithSelection = maskedValue.select(value, { start, end: end + 1 });
                        newValueWithSelection = maskedValue.remove(newValueWithSelection, mask, Direction.Forward);
                    }
                }

                if (handled && newValueWithSelection) {
                    e.preventDefault();
                    this.updateInputState(e, newValueWithSelection);
                }

                if (this.props.onKeyDown) {
                    this.props.onKeyDown(e);
                }
            }
        };

        handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (this.inputElement && e.key && e.key.length === 1) {
                e.preventDefault();
                this.writeChar(e, this.inputElement.value, e.key);
            }
        };

        handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
            if (this.inputElement) {
                e.preventDefault();

                const { value } = this.inputElement;
                const { mask } = this.props;
                let newMaskedValue;

                if (this.isPhoneInput) {
                    this.handlePhoneInput(e);
                } else {
                    if (maskedValue.conformsToMask(value, mask)) {
                        const valueLength = value.length;
                        newMaskedValue = { value, selection: { start: valueLength, end: valueLength } };
                    } else if (looksLikeDateRegExp.test(value)) {
                        newMaskedValue = maskedValue.maskValueAsDate(value, mask);
                    } else {
                        newMaskedValue = maskedValue.maskValue(value, mask);
                    }

                    this.updateInputState(e, newMaskedValue);
                }
            }
        };

        handlePhoneInput = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (this.inputElement) {
                const { value } = this.inputElement;
                const { mask } = this.props;

                this.isPhoneInput = false;
                const { previousValue, previousSelectionStart, previousSelectionEnd } = this.phoneInput;
                let newMaskedValue;

                // value has been removed
                if (previousValue.length > value.length) {
                    const { selectionStart } = this.inputElement;

                    if (previousSelectionStart !== null && selectionStart !== null) {
                        // was selected more than 1 symbol
                        if (previousSelectionStart < previousSelectionEnd) {
                            newMaskedValue = maskedValue.select(this.state.vs.value, {
                                start: previousSelectionStart,
                                end: previousSelectionEnd,
                            });
                            newMaskedValue = maskedValue.remove(newMaskedValue, mask);

                            // value has been removed and 1 symbol was inserted
                            if (previousSelectionStart !== selectionStart) {
                                const { value: newValue } = newMaskedValue;
                                this.writeChar(
                                    e,
                                    newValue,
                                    getFirstValueDifference(newValue, value),
                                    previousSelectionStart,
                                    previousSelectionStart
                                );
                            } else {
                                this.updateInputState(e, newMaskedValue);
                            }
                        } else {
                            newMaskedValue = maskedValue.select(this.state.vs.value, {
                                start: selectionStart,
                                end: previousSelectionEnd,
                            });
                            newMaskedValue = maskedValue.remove(newMaskedValue, mask);

                            this.updateInputState(e, newMaskedValue);
                        }
                    }
                } else {
                    if (looksLikeDateRegExp.test(value)) {
                        newMaskedValue = maskedValue.maskValueAsDate(value, mask);
                    } else {
                        newMaskedValue = maskedValue.maskValue(value, mask);
                    }
                    this.updateInputState(e, newMaskedValue);
                }
            }
        };

        handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            if (!this.state.focused) {
                this.setState({ focused: true });
            }
            if (this.props.onFocus) {
                this.props.onFocus(e);
            }
        };

        handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            if (this.state.focused) {
                this.setState({ focused: false });
            }
            if (this.props.onBlur) {
                this.props.onBlur(e);
            }
        };

        writeChar(
            e: React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>,
            value: string,
            inputChar: string,
            configuredStart?: number,
            configuredEnd?: number
        ) {
            if (this.inputElement) {
                const { mask } = this.props;
                const start = configuredStart !== undefined ? configuredStart : this.inputElement.selectionStart;
                const end = configuredEnd !== undefined ? configuredEnd : this.inputElement.selectionEnd;

                let newMaskedValue;

                if (start === null || end === null) {
                    return;
                }

                newMaskedValue = maskedValue.select(value, { start, end });
                newMaskedValue = maskedValue.input(newMaskedValue, mask, inputChar);

                this.updateInputState(e, newMaskedValue);
            }
        }

        updateInputState(
            e: React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>,
            { value, selection }: ValueWithSelection
        ): void {
            if (this.inputElement) {
                const { inputElement } = this;

                inputElement.value = value;

                this.setState({ vs: { value, selection } });

                if (this.props.onChange) {
                    this.props.onChange(e as React.ChangeEvent<HTMLInputElement>, value);
                }
            }
        }

        inputElementRef = (c: HTMLInputElement) => {
            this.inputElement = c;
            if (this.props.inputElementRef) {
                this.props.inputElementRef(c);
            }
        };

        render() {
            const { mask, showPatternPlaceholder, ...other } = this.props;
            const { value } = this.state.vs;

            other.value = undefined;

            if (showPatternPlaceholder) {
                other.placeholder = undefined;
            }

            const placeholderText = maskedValue.getValueWithPlaceholder(
                { value, selection: { start: 0, end: 0 } },
                mask
            );

            const inputProps = {
                ...other,
                value,
                placeholder: other.placeholder ?? placeholderText,
                onKeyDown: this.handleKeyDown,
                onKeyPress: this.handleKeyPress,
                onInput: this.handleInput,
                onFocus: this.handleFocus,
                onBlur: this.handleBlur,
                ref: this.inputElementRef,
            };

            return (
                <div style={InputContainer}>
                    <InputElement {...inputProps} />
                </div>
            );
        }
    };
